// Estilos base para tablas estandarizadas del sistema
// Basado en WarehouseTable (modelo)
import { styled, alpha } from '@mui/material/styles';
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  IconButton,
} from '@mui/material';
import { colors, spacing, typography } from '../../../styles/theme';
import {
  getTableRowSelectedHoverOverlay,
  getTableRowSelectedOverlay,
} from '../../../styles/theme/tableRowOverlays';
import { TableStatusChip } from './TableStatusChip';
// Export explícito para compatibilidad con imports existentes
export { TableStatusChip as StatusChip };

// ============================================
// CONTAINERS
// ============================================

export const TableContainerStyled = styled(Box)(({ theme }) => ({
  backgroundColor: 'transparent',
  borderRadius: 0,
  border: 'none',
  boxShadow: 'none',
  overflow: 'visible',
}));

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: 'transparent !important',
  border: 'none !important',
  boxShadow: 'none !important',
  borderRadius: '0 !important',
  padding: '0 !important',
  margin: '0 !important',
  // Sobrescribir estilos de Paper cuando se usa como component
  '&.MuiPaper-root': {
    boxShadow: 'none !important',
    backgroundColor: 'transparent !important',
    border: 'none !important',
    borderRadius: '0 !important',
    padding: '0 !important',
    margin: '0 !important',
    backgroundImage: 'none !important',
  },
  // También para cualquier Paper hijo
  '& .MuiPaper-root': {
    boxShadow: 'none !important',
    backgroundColor: 'transparent !important',
    border: 'none !important',
    borderRadius: '0 !important',
    padding: '0 !important',
    margin: '0 !important',
    backgroundImage: 'none !important',
  },
})) as typeof TableContainer;

// ============================================
// TABLE
// ============================================

export const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  borderCollapse: 'separate',
  borderSpacing: 0,
  width: '100%',
  // Asegurar que la tabla no tenga bordes laterales
  borderLeft: 'none !important',
  borderRight: 'none !important',
  // Eliminar bordes laterales de todas las celdas excepto el header
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderLeft: 'none !important',
    borderRight: 'none !important',
  },
  '& .MuiTableRow-root': {
    borderLeft: 'none !important',
    borderRight: 'none !important',
  },
}));

// ============================================
// HEADER
// ============================================

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.03)',
  position: 'relative',
  overflow: 'hidden',
  '& .MuiTableRow-root': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiTableCell-root': {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.9rem',
    letterSpacing: 0,
    textTransform: 'none',
    textAlign: 'left',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: 'none',
    borderLeft: 'none',
    background: 'transparent',
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    lineHeight: 1.4,
  },
}));

export const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: 'none',
  borderLeft: 'none',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  letterSpacing: 0,
  textTransform: 'none',
  lineHeight: 1.4,
  textAlign: 'left',
  '& .MuiTableSortLabel-root': {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
  },
}));

// ============================================
// ROWS
// ============================================

export const StyledTableRow = styled(TableRow)(({ theme }) => {
  const selectedBg = getTableRowSelectedOverlay(theme);
  const selectedHoverBg = getTableRowSelectedHoverOverlay(theme);
  const rowBorder =
    theme.palette.mode === 'dark'
      ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
      : `1px solid ${colors.grey[50]}`;

  return {
    transition: 'background 0.2s ease-in-out',
    backgroundColor: theme.palette.background.paper,
    borderBottom: rowBorder,
    borderLeft: 'none',
    borderRight: 'none',
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.background.paper,
    },
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.background.paper,
    },
    '&.Mui-selected': {
      backgroundColor: `${selectedBg} !important`,
      '&:hover': {
        backgroundColor: `${selectedHoverBg} !important`,
      },
    },
  };
});

// ============================================
// CELLS
// ============================================

type TableCellTone = 'default' | 'muted';

export const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone?: TableCellTone }>(({ theme, tone = 'default' }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderBottom: 'none',
  borderLeft: 'none',
  borderRight: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  lineHeight: 1.5,
  ...(tone === 'muted' && {
    color: theme.palette.text.secondary,
    fontWeight: 400,
  }),
}));

// ============================================
// CHECKBOXES
// ============================================

export const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  opacity: 1,
  color: colors.primary.main,
  padding: theme.spacing(0.5),
  borderRadius: '4px',
  '&.Mui-checked': {
    color: colors.primary.main,
    backgroundColor: alpha(colors.primary.main, 0.1),
  },
  '&.MuiCheckbox-indeterminate': {
    color: colors.primary.main,
    backgroundColor: alpha(colors.primary.main, 0.1),
  },
  '&:hover': {
    backgroundColor: alpha(colors.primary.main, 0.08),
    transform: 'scale(1.05)',
  },
  transition: 'all 0.2s ease-in-out',
}));

export const DisabledCheckbox = styled(Checkbox)(({ theme }) => ({
  opacity: 0.5,
  color: theme.palette.action.disabled,
  cursor: 'not-allowed',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  transition: 'all 0.2s ease-in-out',
}));

// ============================================
// CHIPS
// ============================================

// ============================================
// BUTTONS
// ============================================

export const ViewButton = styled(IconButton)(({ theme }) => ({
  color: colors.primary.main,
  '&:hover': {
    backgroundColor: `${colors.primary.main}20`,
    transform: 'scale(1.1)',
  },
  transition: 'all 0.2s ease-in-out',
}));

type ActionTone = 'primary' | 'error';

export const ActionButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'colorVariant',
})<{ colorVariant?: ActionTone }>(({ colorVariant = 'primary' }) => {
  const tone = colorVariant === 'error' ? colors.error.main : colors.primary.main;
  return {
    color: tone,
    '&:hover': {
      backgroundColor: alpha(tone, 0.12),
      color: tone,
    },
    transition: 'all 0.2s ease-in-out',
  };
});

// ============================================
// EMPTY STATE
// ============================================

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: spacing.xl,
  textAlign: 'center',
}));

export const EmptyStateIcon = styled(Box)(({ theme }) => ({
  fontSize: '4rem',
  color: theme.palette.text.disabled,
  marginBottom: spacing.md,
}));

export const EmptyStateTitle = styled(Typography)(({ theme }) => ({
  fontSize: typography.fontSize.lg,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: spacing.sm,
}));

export const EmptyStateSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: typography.fontSize.sm,
  color: theme.palette.text.secondary,
  marginBottom: spacing.lg,
}));
